import React, { FC, useState } from 'react';
import { AxiosError } from 'axios';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { CreateUserRequest } from '../../../../services/backend/data-contracts';
import UserRoleDetails, { UserDetailsRef } from '../UserRoleDetails/UserRoleDetails';

import useUserModal from './useUserModal';

interface UserModalProps {
  selectedUserId: number;
  handleModalClose: () => void;
}

const UserModal: FC<UserModalProps> = ({ selectedUserId, handleModalClose }) => {
  const [detailsErrors, setDetailsErrors] = useState<string[]>([]);

  const { details, updateUserMutation, isProcessing, createUserMutation } =
    useUserModal(selectedUserId);
  const isNewUser = !selectedUserId;
  const entryRef = React.useRef<UserDetailsRef | null>(null);

  const handleCreateClick = () => {
    // UserRoleDetails shows validation errors inline and the mutation reports save failures,
    // so a rejection needs no further handling here.
    entryRef?.current?.handleSaveClick().catch(() => {});
  };

  const handleCreateUserSaveClick = async (formValues: CreateUserRequest) => {
    createUserMutation.mutate(
      { entry: formValues },
      {
        onSuccess: () => {
          handleModalClose();
          setDetailsErrors([]);
        },
        onError: (e: AxiosError<{ data?: string | string[]; message?: string }>) => {
          // The backend's error responses send `data` as one string, often the same text as `message`.
          const { data, message } = e.response?.data ?? {};
          const errors = [...(Array.isArray(data) ? data : [data]), message].filter(
            (error): error is string => !!error
          );
          setDetailsErrors([...new Set(errors)]);
        },
      }
    );
  };

  const handleSaveClick = (roleId: number, doesUserHaveRole: boolean) => {
    updateUserMutation.mutate({ roleId, doesUserHaveRole });
  };

  const disableCreateButton = detailsErrors.length != 0 || isProcessing;

  return (
    <Dialog fullWidth maxWidth="md" open={true} data-testid="UserModal">
      <DialogTitle>{'User ' + (isNewUser ? 'Create' : 'Update')}</DialogTitle>
      <DialogContent>
        <UserRoleDetails
          selectedUserId={selectedUserId}
          details={details}
          handleCreateUserSaveClick={handleCreateUserSaveClick}
          updateUserRole={handleSaveClick}
          ref={entryRef}
          disableRoleChanges={isProcessing}
        ></UserRoleDetails>
      </DialogContent>
      <DialogActions>
        {detailsErrors.length > 0 ? (
          <div>
            <p>There was an error: </p>
            <ul>
              {detailsErrors.map((detailError, i) => (
                <li key={i}>{detailError}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {isNewUser ? (
          <>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button onClick={handleCreateClick} disabled={disableCreateButton}>
              Create
            </Button>
          </>
        ) : (
          <Button onClick={handleModalClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserModal;
